import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Typography, Chip, IconButton, Avatar, Tooltip, LinearProgress,
  InputBase, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Stack, Divider,
  Skeleton,
} from "@mui/material";
import {
  Refresh, Search, Close, Edit, CheckCircle, Cancel, Save,
  People, Person, DirectionsBike, ChevronRight, ChevronLeft,
  Delete, Warning, PersonAdd, AdminPanelSettings, FiberManualRecord,
  PointOfSale,
} from "@mui/icons-material";
import api from "../../api/api";
import { useSnackbar } from "../../utils/snackbar";

// ── Theme ────────────────────────────────────────────────────────────────────
const BRAND_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const PAGE_SIZE = 20;

const AVATAR_COLORS = ["#667eea", "#22c55e", "#f59e0b", "#ef4444", "#3b82f6", "#f472b6", "#a78bfa"];
const avatarColor = (str) => AVATAR_COLORS[(str || "?").charCodeAt(0) % AVATAR_COLORS.length];

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.round((Date.now() - new Date(iso)) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 30)  return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

const FILTER_DEFS = [
  { id: "verified",   label: "Verified"   },
  { id: "unverified", label: "Unverified" },
  { id: "rider",      label: "Riders"     },
  { id: "customer",   label: "Customers"  },
  { id: "cashier",    label: "Cashiers"   },
  { id: "inactive",   label: "Inactive"   },
  { id: "admin",      label: "Admins"     },
  { id: "online",     label: "Online"     },
];

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ title, value, icon, gradient }) {
  return (
    <Paper sx={{ flex: 1, minWidth: 130, p: 2, borderRadius: 3, position: "relative", overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: gradient, borderRadius: "12px 12px 0 0" }} />
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={900} lineHeight={1.1}>{value}</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

// ── Action button in detail panel ─────────────────────────────────────────────
function ActionBtn({ label, color, loading, onClick }) {
  return (
    <Box onClick={loading ? undefined : onClick} sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      px: 1.5, py: 1, borderRadius: 1.5, cursor: loading ? "default" : "pointer",
      border: "1px solid", borderColor: `${color}33`,
      transition: "all 0.15s",
      "&:hover": loading ? {} : { bgcolor: `${color}11` },
    }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: loading ? "text.disabled" : color }}>{label}</Typography>
      {loading
        ? <Box sx={{ width: 13, height: 13, borderRadius: "50%", border: `2px solid ${color}33`, borderTopColor: color, animation: "spin 0.6s linear infinite" }} />
        : <ChevronRight sx={{ fontSize: 14, color: `${color}66` }} />}
    </Box>
  );
}

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────
function DeleteDialog({ user, onClose, onConfirm, loading }) {
  if (!user) return null;
  return (
    <Dialog open maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Warning color="error" /> Delete User
      </DialogTitle>
      <DialogContent>
        <Typography>
          Permanently delete <strong>{user.name || user.email}</strong>? This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={loading}>
          {loading ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Create User Dialog ────────────────────────────────────────────────────────
const EMPTY_CREATE = { name: "", email: "", username: "", phone: "", password: "", role: "customer" };

function CreateUserDialog({ open, onClose, onCreate, loading }) {
  const [form, setForm] = useState(EMPTY_CREATE);
  useEffect(() => { if (open) setForm(EMPTY_CREATE); }, [open]);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={800}>Invite New User</Typography>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <TextField label="Full Name" value={form.name} onChange={set("name")} fullWidth autoFocus />
          <TextField label="Email" type="email" value={form.email} onChange={set("email")} fullWidth />
          <TextField
            label="Username" value={form.username} onChange={set("username")} fullWidth
            helperText={form.role === "cashier" ? "How this cashier signs into the POS till" : "Optional — lets this account sign into the POS with just a username"}
          />
          <TextField label="Phone" value={form.phone} onChange={set("phone")} fullWidth />
          <TextField label="Password" type="password" value={form.password} onChange={set("password")} fullWidth helperText="Minimum 6 characters" />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={form.role} label="Role" onChange={set("role")}>
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="rider">Rider</MenuItem>
              <MenuItem value="cashier">Cashier</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => onCreate(form)} disabled={loading}
          sx={{ background: BRAND_GRADIENT, fontWeight: 700 }}>
          {loading ? "Creating…" : "Create User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function ManageUsers() {
  const { showAlert } = useSnackbar();

  const [users, setUsers]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState("");
  const [selectedTags, setSelectedTags]   = useState([]);
  const [page, setPage]                   = useState(1);
  const [selectedUser, setSelectedUser]   = useState(null);
  const [isEditing, setIsEditing]         = useState(false);
  const [editValues, setEditValues]       = useState({});
  const [editLoading, setEditLoading]     = useState(false);
  const [showPw, setShowPw]               = useState(false);
  const [pwForm, setPwForm]               = useState({ newPassword: "", confirmPassword: "" });
  const [pwLoading, setPwLoading]         = useState(false);
  const [bulkSelected, setBulkSelected]   = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [deleteUser, setDeleteUser]       = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createOpen, setCreateOpen]       = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/users");
      const data = res.data?.users || res.data || [];
      const sorted = [...data].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setUsers(sorted);
      return sorted;
    } catch {
      showAlert("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     users.length,
    admins:    users.filter(u => u.isAdmin || u.role === "admin").length,
    riders:    users.filter(u => u.role === "rider").length,
    cashiers:  users.filter(u => u.role === "cashier").length,
    online:    users.filter(u => u.isOnline).length,
    suspended: users.filter(u => u.active === false).length,
  }), [users]);

  // ── Filtered + paginated ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let t = [...users];
    if (search.trim()) {
      const q = search.toLowerCase();
      t = t.filter(u => `${u.name || ""} ${u.email || ""} ${u.phone || ""} ${u.username || ""}`.toLowerCase().includes(q));
    }
    selectedTags.forEach(tag => {
      if (tag === "verified")   t = t.filter(u => u.verified);
      if (tag === "unverified") t = t.filter(u => !u.verified);
      if (tag === "rider")      t = t.filter(u => u.role === "rider");
      if (tag === "customer")   t = t.filter(u => !u.isAdmin && u.role !== "rider" && u.role !== "cashier");
      if (tag === "cashier")    t = t.filter(u => u.role === "cashier");
      if (tag === "inactive")   t = t.filter(u => u.active === false);
      if (tag === "admin")      t = t.filter(u => u.isAdmin || u.role === "admin");
      if (tag === "online")     t = t.filter(u => u.isOnline);
    });
    return t;
  }, [users, search, selectedTags]);

  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const pageNums = useMemo(() => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i).filter(n => n <= totalPages);
  }, [page, totalPages]);

  // ── Action helper ──────────────────────────────────────────────────────────
  const runAction = useCallback(async (id, type, fn) => {
    setActionLoading(p => ({ ...p, [id]: type }));
    try {
      await fn();
      const fresh = await fetchUsers();
      if (selectedUser?._id === id && fresh) {
        setSelectedUser(fresh.find(u => u._id === id) || null);
      }
      showAlert("Done!", "success");
    } catch {
      showAlert("Action failed", "error");
    } finally {
      setActionLoading(p => ({ ...p, [id]: null }));
    }
  }, [selectedUser, fetchUsers, showAlert]);

  // ── Detail panel ───────────────────────────────────────────────────────────
  const openDetail = (u) => {
    setSelectedUser(u);
    setIsEditing(false);
    setEditValues({ name: u.name || "", email: u.email || "", username: u.username || "", phone: u.phone || "", role: u.role || "customer" });
    setPwForm({ newPassword: "", confirmPassword: "" });
    setShowPw(false);
  };

  const saveEdit = async () => {
    setEditLoading(true);
    try {
      await api.put(`/api/admin/users/${selectedUser._id}`, editValues);
      showAlert("User updated", "success");
      setIsEditing(false);
      const fresh = await fetchUsers();
      if (fresh) setSelectedUser(fresh.find(u => u._id === selectedUser._id) || null);
    } catch {
      showAlert("Update failed", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const resetPassword = async () => {
    const { newPassword, confirmPassword } = pwForm;
    if (!newPassword || newPassword.length < 6) return showAlert("Minimum 6 characters", "error");
    if (newPassword !== confirmPassword) return showAlert("Passwords don't match", "error");
    setPwLoading(true);
    try {
      await api.patch(`/api/admin/users/${selectedUser._id}/password`, { newPassword });
      showAlert("Password reset!", "success");
      setShowPw(false);
      setPwForm({ newPassword: "", confirmPassword: "" });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Reset failed", "error");
    } finally {
      setPwLoading(false);
    }
  };

  // ── Bulk ───────────────────────────────────────────────────────────────────
  const bulkRun = async (type, fn) => {
    for (const u of users.filter(u => bulkSelected.includes(u._id))) {
      await runAction(u._id, type, () => fn(u));
    }
    setBulkSelected([]);
  };

  // ── Create ─────────────────────────────────────────────────────────────────
  const handleCreate = async (form) => {
    if (!form.name.trim() || !form.email.trim() || !form.password || form.password.length < 6)
      return showAlert("Name, email and password (min 6 chars) required", "error");
    setCreateLoading(true);
    try {
      await api.post("/api/admin/users", form);
      showAlert("User created", "success");
      setCreateOpen(false);
      fetchUsers();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Create failed", "error");
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/admin/users/${deleteUser._id}`);
      showAlert("User deleted", "success");
      if (selectedUser?._id === deleteUser._id) setSelectedUser(null);
      setDeleteUser(null);
      fetchUsers();
    } catch {
      showAlert("Delete failed", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={900}>User Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {users.length} total · {stats.online} online · {stats.suspended} suspended
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchUsers} disabled={loading} sx={{ borderRadius: 2 }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setCreateOpen(true)}
            sx={{ background: BRAND_GRADIENT, fontWeight: 700, borderRadius: 2 }}>
            Add User
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Stat Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
        <StatCard title="Total Users"  value={stats.total}     icon={<People />}             gradient={BRAND_GRADIENT} />
        <StatCard title="Admins"       value={stats.admins}    icon={<AdminPanelSettings />} gradient="linear-gradient(135deg,#ef4444,#dc2626)" />
        <StatCard title="Riders"       value={stats.riders}    icon={<DirectionsBike />}     gradient="linear-gradient(135deg,#f59e0b,#d97706)" />
        <StatCard title="Cashiers"     value={stats.cashiers}  icon={<PointOfSale />}        gradient="linear-gradient(135deg,#3b82f6,#2563eb)" />
        <StatCard title="Online"       value={stats.online}    icon={<FiberManualRecord />}  gradient="linear-gradient(135deg,#22c55e,#16a34a)" />
        <StatCard title="Suspended"    value={stats.suspended} icon={<Cancel />}             gradient="linear-gradient(135deg,#64748b,#475569)" />
      </Stack>

      {/* Content */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>

        {/* ── Table ──────────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>

            {/* Search + filters */}
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <Box sx={{ flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
                  <Search sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
                  <InputBase
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Search name, email, phone…"
                    sx={{ flex: 1, fontSize: 13 }}
                  />
                  {search && <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.3 }}><Close sx={{ fontSize: 13 }} /></IconButton>}
                </Box>

                {/* Bulk actions bar */}
                {bulkSelected.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" fontWeight={700} color="primary.main">
                      {bulkSelected.length} selected
                    </Typography>
                    {[
                      { label: "Verify",  color: "success", fn: () => bulkRun("verify",  u => api.patch(`/api/admin/users/${u._id}/verify`))  },
                      { label: "Suspend", color: "warning", fn: () => bulkRun("status",  u => api.patch(`/api/admin/users/${u._id}/status`))  },
                      { label: "Delete",  color: "error",   fn: () => bulkRun("delete",  u => api.delete(`/api/admin/users/${u._id}`))         },
                    ].map(b => (
                      <Button key={b.label} size="small" color={b.color} variant="outlined" onClick={b.fn}
                        sx={{ minWidth: 0, px: 1.5, fontSize: 11, fontWeight: 700 }}>
                        {b.label}
                      </Button>
                    ))}
                    <IconButton size="small" onClick={() => setBulkSelected([])}><Close sx={{ fontSize: 13 }} /></IconButton>
                  </Stack>
                )}
              </Box>

              {/* Filter chips */}
              <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                {FILTER_DEFS.map(f => {
                  const active = selectedTags.includes(f.id);
                  return (
                    <Chip key={f.id} label={f.label} size="small" onClick={() => {
                      setSelectedTags(prev => prev.includes(f.id) ? prev.filter(t => t !== f.id) : [...prev, f.id]);
                      setPage(1);
                    }} sx={{
                      height: 26, fontSize: 11, fontWeight: 600, cursor: "pointer",
                      bgcolor: active ? "primary.main" : "action.hover",
                      color: active ? "#fff" : "text.secondary",
                    }} />
                  );
                })}
              </Box>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell padding="checkbox" sx={{ pl: 2, width: 48 }}>
                      <Box
                        onClick={() => setBulkSelected(prev => prev.length === filtered.length ? [] : filtered.map(u => u._id))}
                        sx={{ width: 16, height: 16, borderRadius: 0.8, border: "1.5px solid", cursor: "pointer",
                          borderColor: bulkSelected.length === filtered.length && filtered.length > 0 ? "primary.main" : "divider",
                          bgcolor: bulkSelected.length === filtered.length && filtered.length > 0 ? "primary.main" : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                        {bulkSelected.length === filtered.length && filtered.length > 0 &&
                          <CheckCircle sx={{ fontSize: 12, color: "#fff" }} />}
                      </Box>
                    </TableCell>
                    {["User", "Contact", "Role", "Status", "Joined", ""].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: "text.secondary", py: 1.2 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(8)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton variant="circular" width={16} height={16} /></TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Skeleton variant="rounded" width={36} height={36} />
                            <Box><Skeleton width={120} height={14} /><Skeleton width={60} height={10} /></Box>
                          </Stack>
                        </TableCell>
                        <TableCell><Skeleton width={140} height={14} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={64} height={20} /></TableCell>
                        <TableCell><Skeleton variant="rounded" width={60} height={20} /></TableCell>
                        <TableCell><Skeleton width={70} height={14} /></TableCell>
                        <TableCell><Skeleton width={60} height={28} /></TableCell>
                      </TableRow>
                    ))
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", py: 8 }}>
                        <Typography color="text.secondary">No users match your filters</Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginated.map(u => {
                    const isSelected = selectedUser?._id === u._id;
                    const isBulked   = bulkSelected.includes(u._id);
                    const ac         = avatarColor(u.name || u.email);
                    const roleLabel  = u.isAdmin ? "Admin" : u.role === "rider" ? "Rider" : u.role === "cashier" ? "Cashier" : "Customer";
                    const roleColor  = u.isAdmin ? "error" : u.role === "rider" ? "secondary" : u.role === "cashier" ? "info" : "default";

                    return (
                      <TableRow key={u._id} onClick={() => openDetail(u)} hover sx={{
                        cursor: "pointer",
                        bgcolor: isSelected ? "primary.50" : "inherit",
                        borderLeft: isSelected ? "3px solid" : "3px solid transparent",
                        borderLeftColor: isSelected ? "primary.main" : "transparent",
                        transition: "border-color 0.12s",
                      }}>
                        <TableCell padding="checkbox" sx={{ pl: 2 }}>
                          <Box onClick={e => {
                            e.stopPropagation();
                            setBulkSelected(prev => prev.includes(u._id) ? prev.filter(id => id !== u._id) : [...prev, u._id]);
                          }} sx={{ width: 16, height: 16, borderRadius: 0.8, border: "1.5px solid", cursor: "pointer",
                            borderColor: isBulked ? "primary.main" : "divider",
                            bgcolor: isBulked ? "primary.main" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {isBulked && <CheckCircle sx={{ fontSize: 12, color: "#fff" }} />}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{ position: "relative" }}>
                              <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: `${ac}22`, color: ac, fontSize: 14, fontWeight: 700, borderRadius: 1.5 }}>
                                {(u.name || u.email || "?").charAt(0).toUpperCase()}
                              </Avatar>
                              {u.isOnline && (
                                <FiberManualRecord sx={{ position: "absolute", bottom: -2, right: -2, fontSize: 11, color: "success.main" }} />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight={700} noWrap>{u.name || "—"}</Typography>
                              {u.verified && <Typography sx={{ fontSize: 9, fontWeight: 700, color: "success.main", letterSpacing: 0.3 }}>✓ VERIFIED</Typography>}
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap>{u.email || "—"}</Typography>
                          {u.username && <Typography variant="caption" color="text.secondary" display="block">@{u.username}</Typography>}
                          {u.phone && <Typography variant="caption" color="text.secondary">{u.phone}</Typography>}
                        </TableCell>

                        <TableCell>
                          <Chip label={roleLabel} color={roleColor} size="small" sx={{ fontWeight: 700, fontSize: 10, height: 20 }} />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={u.active !== false ? "Active" : "Suspended"}
                            color={u.active !== false ? "success" : "error"}
                            size="small" sx={{ fontWeight: 700, fontSize: 10, height: 20 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{timeAgo(u.createdAt)}</Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                            <Tooltip title="Edit">
                              <IconButton size="small" color="primary" onClick={() => openDetail(u)}>
                                <Edit sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteUser(u)}>
                                <Delete sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary">
                {filtered.length} users · page {page} of {totalPages}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)} sx={{ borderRadius: 1.5 }}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                {pageNums.map(n => (
                  <Box key={n} onClick={() => setPage(n)} sx={{
                    width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: 1.5, cursor: "pointer", fontSize: 12, fontWeight: n === page ? 800 : 400,
                    bgcolor: n === page ? "primary.main" : "action.hover",
                    color: n === page ? "#fff" : "text.secondary",
                  }}>{n}</Box>
                ))}
                <IconButton size="small" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} sx={{ borderRadius: 1.5 }}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* ── Detail Panel ──────────────────────────────────────────────────── */}
        {selectedUser && (
          <Box sx={{ width: 300, flexShrink: 0, position: "sticky", top: 20 }}>
            <Paper sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ height: 3, background: BRAND_GRADIENT }} />
              <Box sx={{ p: 2.5 }}>

                {/* Panel header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                    User Details
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={isEditing ? "Cancel" : "Edit"}>
                      <IconButton size="small" color={isEditing ? "warning" : "primary"} onClick={() => setIsEditing(!isEditing)}>
                        {isEditing ? <Close sx={{ fontSize: 15 }} /> : <Edit sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteUser(selectedUser)}>
                        <Delete sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => { setSelectedUser(null); setIsEditing(false); }}>
                      <Close sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Stack>
                </Box>

                {/* Avatar */}
                <Box sx={{ textAlign: "center", mb: 2.5, pb: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <Avatar variant="rounded" sx={{
                      width: 64, height: 64, fontSize: 22, fontWeight: 900, mb: 1.5,
                      bgcolor: `${avatarColor(selectedUser.name || selectedUser.email)}22`,
                      color: avatarColor(selectedUser.name || selectedUser.email),
                      borderRadius: 3, border: "2px solid", borderColor: "divider",
                    }}>
                      {(selectedUser.name || selectedUser.email || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    {selectedUser.isOnline && (
                      <FiberManualRecord sx={{ position: "absolute", bottom: 14, right: -4, fontSize: 14, color: "success.main" }} />
                    )}
                  </Box>
                  <Typography fontWeight={800} noWrap>{selectedUser.name || "No name"}</Typography>
                  <Stack direction="row" spacing={0.7} justifyContent="center" mt={0.8} flexWrap="wrap">
                    <Chip
                      label={selectedUser.isAdmin ? "Admin" : selectedUser.role === "rider" ? "Rider" : selectedUser.role === "cashier" ? "Cashier" : "Customer"}
                      color={selectedUser.isAdmin ? "error" : selectedUser.role === "rider" ? "secondary" : selectedUser.role === "cashier" ? "info" : "default"}
                      size="small" sx={{ fontWeight: 700, fontSize: 10, height: 20 }}
                    />
                    {selectedUser.verified && <Chip label="Verified" color="success" size="small" sx={{ fontWeight: 700, fontSize: 10, height: 20 }} />}
                    <Chip
                      label={selectedUser.active !== false ? "Active" : "Suspended"}
                      color={selectedUser.active !== false ? "success" : "error"}
                      size="small" sx={{ fontWeight: 700, fontSize: 10, height: 20 }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Joined {timeAgo(selectedUser.createdAt)}
                  </Typography>
                </Box>

                {/* Edit mode */}
                {isEditing ? (
                  <Box>
                    <Stack spacing={1.5} mb={2}>
                      {[
                        { label: "Full Name", key: "name",     type: "text"  },
                        { label: "Email",     key: "email",    type: "email" },
                        { label: "Username",  key: "username", type: "text"  },
                        { label: "Phone",     key: "phone",    type: "text"  },
                      ].map(f => (
                        <TextField key={f.key} label={f.label} type={f.type} size="small" fullWidth
                          value={editValues[f.key]}
                          onChange={e => setEditValues(p => ({ ...p, [f.key]: e.target.value }))}
                        />
                      ))}
                      <FormControl fullWidth size="small">
                        <InputLabel>Role</InputLabel>
                        <Select value={editValues.role} label="Role" onChange={e => setEditValues(p => ({ ...p, role: e.target.value }))}>
                          <MenuItem value="customer">Customer</MenuItem>
                          <MenuItem value="rider">Rider</MenuItem>
                          <MenuItem value="cashier">Cashier</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" fullWidth size="small" onClick={() => setIsEditing(false)} disabled={editLoading}>Cancel</Button>
                      <Button variant="contained" fullWidth size="small" startIcon={<Save />} onClick={saveEdit} disabled={editLoading}
                        sx={{ background: BRAND_GRADIENT }}>
                        {editLoading ? "Saving…" : "Save"}
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <>
                    {/* Info rows */}
                    {[
                      { label: "Email",    value: selectedUser.email || "—" },
                      { label: "Username", value: selectedUser.username ? `@${selectedUser.username}` : "—" },
                      { label: "Phone",    value: selectedUser.phone || "—" },
                      { label: "ID",       value: `…${(selectedUser._id || "").slice(-10).toUpperCase()}` },
                    ].map(r => (
                      <Box key={r.label} sx={{ display: "flex", justifyContent: "space-between", py: 0.8, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{r.label}</Typography>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 170, textAlign: "right" }}>{r.value}</Typography>
                      </Box>
                    ))}

                    {/* Quick Actions */}
                    <Box mt={2}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1 }}>
                        Quick Actions
                      </Typography>
                      <Stack spacing={0.8}>
                        <ActionBtn
                          label={selectedUser.verified ? "Unverify User" : "Verify User"}
                          color={selectedUser.verified ? "#f59e0b" : "#22c55e"}
                          loading={actionLoading[selectedUser._id] === "verify"}
                          onClick={() => runAction(selectedUser._id, "verify",
                            () => selectedUser.verified
                              ? api.patch(`/api/admin/users/${selectedUser._id}/unverify`)
                              : api.patch(`/api/admin/users/${selectedUser._id}/verify`)
                          )}
                        />
                        {!selectedUser.isAdmin && (
                          <ActionBtn
                            label={selectedUser.role === "rider" ? "Make Customer" : "Promote to Rider"}
                            color="#667eea"
                            loading={actionLoading[selectedUser._id] === "role"}
                            onClick={() => runAction(selectedUser._id, "role",
                              () => selectedUser.role === "rider"
                                ? api.patch(`/api/admin/users/${selectedUser._id}/make-customer`)
                                : api.patch(`/api/admin/users/${selectedUser._id}/promote-rider`)
                            )}
                          />
                        )}
                        <ActionBtn
                          label={selectedUser.active !== false ? "Suspend User" : "Activate User"}
                          color={selectedUser.active !== false ? "#f59e0b" : "#22c55e"}
                          loading={actionLoading[selectedUser._id] === "status"}
                          onClick={() => runAction(selectedUser._id, "status",
                            () => api.patch(`/api/admin/users/${selectedUser._id}/status`)
                          )}
                        />
                        <ActionBtn
                          label={showPw ? "Hide Password Form" : "Change Password"}
                          color="#3b82f6"
                          onClick={() => setShowPw(!showPw)}
                        />
                        <ActionBtn
                          label="Delete User"
                          color="#ef4444"
                          loading={actionLoading[selectedUser._id] === "delete"}
                          onClick={() => setDeleteUser(selectedUser)}
                        />
                      </Stack>

                      {/* Password form */}
                      {showPw && (
                        <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: "primary.50", border: "1px solid", borderColor: "primary.100" }}>
                          <Typography variant="caption" fontWeight={700} color="primary.main" display="block" mb={1.5}>
                            Set New Password
                          </Typography>
                          <Stack spacing={1}>
                            {[
                              { key: "newPassword",     label: "New password"     },
                              { key: "confirmPassword", label: "Confirm password" },
                            ].map(f => (
                              <TextField key={f.key} size="small" type="password" label={f.label} fullWidth
                                value={pwForm[f.key]}
                                onChange={e => setPwForm(p => ({ ...p, [f.key]: e.target.value }))}
                                onKeyDown={e => e.key === "Enter" && resetPassword()}
                              />
                            ))}
                            <Button variant="contained" size="small" fullWidth onClick={resetPassword} disabled={pwLoading}
                              sx={{ background: "linear-gradient(135deg,#3b82f6,#2563eb)", fontWeight: 700 }}>
                              {pwLoading ? "Resetting…" : "Reset Password"}
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Dialogs */}
      <DeleteDialog user={deleteUser} onClose={() => setDeleteUser(null)} onConfirm={handleDelete} loading={deleteLoading} />
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} loading={createLoading} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}
